import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { BellRinging, Check, ArrowCounterClockwise } from '@phosphor-icons/react';
import { API } from '../config/api';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const STEPS = [
  { n: 1, days: 'first_days', subject: 'first_subject', body: 'first_body', title: '1° sollecito' },
  { n: 2, days: 'second_days', subject: 'second_subject', body: 'second_body', title: '2° sollecito' },
];

/** Settings → Email: automatic follow-up emails to the client after a quote. */
export default function ClientFollowupsSettings() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/client-followups`).then(({ data }) => setCfg(data)).catch(() => setCfg(null));
  }, []);

  if (!cfg) return null;
  const set = (patch) => setCfg({ ...cfg, ...patch });

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        enabled: cfg.enabled,
        first_days: parseInt(cfg.first_days, 10) || 3,
        second_days: parseInt(cfg.second_days, 10) || 7,
        first_subject: cfg.first_subject, first_body: cfg.first_body,
        second_subject: cfg.second_subject, second_body: cfg.second_body,
      };
      const { data } = await axios.put(`${API}/settings/client-followups`, payload);
      setCfg({ ...cfg, ...data });
      toast.success('Solleciti automatici salvati');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border border-[#E2E8F0] rounded-sm" data-testid="client-followups-settings">
      <CardHeader className="border-b border-[#E2E8F0] pb-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <CardTitle className="font-outfit text-lg font-semibold text-[#0A0F1A] flex items-center gap-2">
              <BellRinging size={20} className="text-[#0033A0]" />
              Solleciti automatici ai clienti
            </CardTitle>
            <p className="text-sm text-[#4A5568] mt-1">
              Dopo l'invio del preventivo per email, il cliente riceve due promemoria. Partono solo dal lunedì al venerdì
              tra le 9 e le 18, e si fermano se il preventivo viene accettato, rifiutato o scade. Le risposte arrivano alla tua email aziendale.
            </p>
          </div>
          <Switch
            checked={!!cfg.enabled}
            onCheckedChange={(v) => set({ enabled: v })}
            aria-label="Attiva i solleciti automatici"
            data-testid="client-followups-enabled"
          />
        </div>
      </CardHeader>
      <CardContent className="p-6 space-y-6">
        {!cfg.email_configured && (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-sm p-3">
            L'invio email non è ancora attivo: vedi il riquadro "Invio email" qui sopra.
          </p>
        )}
        {STEPS.map(s => (
          <div key={s.n} className={`space-y-3 ${cfg.enabled ? '' : 'opacity-50'}`}>
            <div className="flex items-center gap-2 flex-wrap">
              <h4 className="font-outfit text-base font-semibold text-[#0A0F1A]">{s.title}</h4>
              <span className="text-sm text-[#4A5568]">dopo</span>
              <Input
                type="number" min={1} max={s.n === 1 ? 60 : 90}
                value={cfg[s.days]}
                onChange={(e) => set({ [s.days]: e.target.value })}
                className="w-20 h-9 text-center"
                data-testid={`client-followups-days-${s.n}`}
              />
              <span className="text-sm text-[#4A5568]">giorni dall'invio</span>
            </div>
            <div>
              <Label className="text-xs text-[#4A5568]">Oggetto</Label>
              <Input value={cfg[s.subject]} onChange={(e) => set({ [s.subject]: e.target.value })} className="mt-1" data-testid={`client-followups-subject-${s.n}`} />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="text-xs text-[#4A5568]">Testo</Label>
                <button
                  type="button"
                  onClick={() => set({ [s.subject]: cfg.defaults[s.subject], [s.body]: cfg.defaults[s.body] })}
                  className="text-xs text-[#0033A0] inline-flex items-center gap-1 hover:underline"
                >
                  <ArrowCounterClockwise size={12} /> Ripristina testo originale
                </button>
              </div>
              <Textarea
                value={cfg[s.body]}
                onChange={(e) => set({ [s.body]: e.target.value })}
                rows={8}
                className="mt-1 text-sm"
                data-testid={`client-followups-body-${s.n}`}
              />
            </div>
          </div>
        ))}
        <p className="text-xs text-[#4A5568]">
          Campi che vengono sostituiti in automatico:{' '}
          {(cfg.placeholders || []).map(p => <code key={p} className="mx-0.5 px-1 bg-[#F4F6F8] rounded">{`{${p}}`}</code>)}
        </p>
        <Button onClick={save} disabled={saving} className="bg-[#0033A0] hover:bg-[#0047AB] text-white" data-testid="client-followups-save">
          <Check size={18} className="mr-2" />{saving ? 'Salvataggio...' : 'Salva solleciti'}
        </Button>
      </CardContent>
    </Card>
  );
}
